import "./MatchStatsModal.css";
import imageBallon from "../images/image-ballon.png";
import API from "../services/api";
import { useEffect, useState } from "react";

function MatchStatsModal({ match, onClose }) {
  const [statistics, setStatistics] = useState([]);
  const [lineups, setLineups] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  // Fonctions de chargement des données
  const loadStatistics = async (matchId) => {
    try {
      const res = await API.get(`/matches/${matchId}/statistics`);
      setStatistics(res.data);
    } catch (error) {
      console.error("Erreur stats:", error);
    }
  };

  const loadLineups = async (matchId) => {
    try {
      const res = await API.get(`/matches/${matchId}/lineups`);
      setLineups(res.data);
    } catch (error) {
      console.error("Erreur compos:", error);
    }
  };

  // 🔄 Effet principal : Gestion de l'actualisation automatique chaque minute (60000ms)
  useEffect(() => {
    if (!match?._id) return;

    // 1. Chargement initial au montage de la modal
    loadLineups(match._id);
    loadStatistics(match._id);

    // 2. Création du timer d'actualisation en tâche de fond
    const interval = setInterval(() => {
      loadLineups(match._id);
      loadStatistics(match._id);
      console.log("Données du match actualisées !");
    }, 60000); // 60 000 millisecondes = 1 minute

    // Nettoyage du timer si l'utilisateur ferme la modal
    return () => clearInterval(interval);
  }, [match?._id]);

  if (!match) return null;

  const getEventIcon = (event) => {
    if (event.type === "Goal") {
      switch (event.detail) {
        case "Normal Goal": return "⚽";
        case "Penalty": return "⚽🎯";
        case "Missed Penalty": return "❌🎯";
        case "Own Goal": return "🥅";
        default: return "⚽";
      }
    }

    if (event.type === "Var") {
      const detail = event.detail?.toLowerCase() || "";
      if (detail.includes("goal cancelled")) return "🚫⚽";
      if (detail.includes("goal confirmed")) return "✅⚽";
      if (detail.includes("penalty confirmed")) return "✅🎯";
      if (detail.includes("penalty cancelled")) return "❌🎯";
      if (detail.includes("red card")) return "📺🟥";
      return "📺";
    }

    if (event.type === "Card") {
      if (event.detail === "Red Card" || event.detail === "Second Yellow Card") {
        return "🟥";
      }
      return "🟨";
    }

    if (event.type === "subst") return "🔄";
    return "•";
  };

  const getEventClass = (event) => {
    if (event.type === "Goal") {
      if (event.detail === "Missed Penalty") return "event-orange";
      return "event-green";
    }

    if (event.type === "Var") {
      const detail = event.detail?.toLowerCase() || "";
      if (detail.includes("cancel")) return "event-red";
      if (detail.includes("confirm")) return "event-green";
    }

    if (event.type === "Card") {
      if (event.detail === "Red Card" || event.detail === "Second Yellow Card") {
        return "event-red";
      }
      return "event-yellow";
    }

    if (event.type === "subst") return "event-blue";
    return "";
  };

  const getStatValue = (team, statType) => {
    if (!team || !team.statistics) return 0;
    const stat = team.statistics.find(s => s.type === statType);
    return stat?.value ?? 0;
  };

  const liveStatuses = ["1H", "HT", "2H", "ET", "BT", "P"];
  const sortedEvents = match.events
    ? [...match.events].sort((a, b) => (a.time || 0) - (b.time || 0))
    : [];

  // Déterminer s'il faut afficher la section mi-temps (uniquement après la 1ère mi-temps)
  const showHalfTime = ["HT", "2H", "ET", "BT", "P", "FT"].includes(match.liveStatus);

  return (
    <div className="stats-overlay">
      <div className="stats-modal">
        <button className="close-btn" onClick={onClose}>✕</button>

        <div className="modal-header" style={{ '--bg-ballon': `url(${imageBallon})` }}>
          <div className="modal-header-teams">
            <div className="team-name home">{match.homeTeam}</div>
            <div className="vs-text">vs</div>
            <div className="team-name away">{match.awayTeam}</div>
          </div>

          <div className="score-box">
            {/* PASTILLE DE STATUT DU TEMPS */}
            <div className="match-minute">
              {liveStatuses.includes(match.liveStatus) && match.liveStatus !== "HT"
                ? `🔴 ${match.elapsed}'`
                : match.liveStatus === "HT"
                ? "🟡 MT"
                : match.liveStatus === "FT"
                ? "⚫ FT"
                : "🟢 NS"}
            </div>

            {/* SI LE MATCH A COMMENCÉ, ON AFFICHE LE SCORE GLOBAL */}
            {match.liveStatus !== "NS" ? (
              <>
                <span>{match.goalsHome ?? 0}</span>
                <span>-</span>
                <span>{match.goalsAway ?? 0}</span>
                
                {/* ON AFFICHE LA ZONE DE MI-TEMPS UNIQUEMENT SI ON A DÉPASSÉ LA MI-TEMPS */}
                {showHalfTime && (match.htGoalsHome !== null && match.htGoalsAway !== null) && (
                  <div className="halftime-modal">
                    MT : {match.htGoalsHome} - {match.htGoalsAway}
                  </div>
                )}
              </>
            ) : (
              // SI LE MATCH N'A PAS COMMENCÉ : Pas de score affiché
              <div className="not-started-text" style={{ fontSize: "14px", marginTop: "10px", color: "#888" }}>
                Match non commencé
              </div>
            )}
          </div>
          <h3>Événements du match</h3>
        </div>

        <div className="match-tabs">
          <button className={activeTab === "overview" ? "active" : ""} onClick={() => setActiveTab("overview")}>
            APERÇU
          </button>
          <button className={activeTab === "stats" ? "active" : ""} onClick={() => setActiveTab("stats")}>
            STATISTIQUES
          </button>
          <button className={activeTab === "lineups" ? "active" : ""} onClick={() => setActiveTab("lineups")}>
            COMPOSITIONS
          </button>
        </div>
           
        {activeTab === "overview" && (
          <div className="events-container">
            {sortedEvents.map((event, index) => {
              const isHomeTeam = event.team === match.homeTeam;
              const eventColorClass = getEventClass(event);
              const isSubstitution = event.type === "subst";

              return (
                <div key={index} className={`event-row ${eventColorClass}`}>
                  <div className="event-home">
                    {isHomeTeam && (
                      isSubstitution ? (
                        <div>
                          <strong>🔄 Remplacement</strong><br />
                          <span className="player-out">⬇️ {event.player}</span><br />
                          <span className="player-in">⬆️ {event.assist || "?"}</span>
                        </div>
                      ) : (
                        <div>
                          <strong>{getEventIcon(event)} {event.player}</strong><br />
                          <div className="event-detail">
                            <small>{event.detail}</small>
                            {event.type === "Goal" && event.assist && (
                              <div className="event-assist">🎯 Passe : {event.assist}</div>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  <div className="event-minute">{event.time}'</div>

                  <div className="event-away">
                    {!isHomeTeam && (
                      isSubstitution ? (
                        <div>
                          <strong>🔄 Remplacement</strong><br />
                          <span className="player-out">⬇️ {event.player}</span><br />
                          <span className="player-in">⬆️ {event.assist || "?"}</span>
                        </div>
                      ) : (
                        <div>
                          <strong>{event.player} {getEventIcon(event)}</strong><br />
                          <small className="event-detail">{event.detail}</small>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === "lineups" && (
          <div className="lineups-tab">
            {Array.isArray(lineups) && lineups.length > 0 ? (
              lineups.map(team => (
                <div key={team.team.id} className="team-lineup">
                  <h3>{team.team.name}</h3>
                  <p>Formation : {team.formation || "N/A"}</p>
                  <p>Coach : {team.coach?.name || "N/A"}</p>

                  <h4>Titulaires</h4>
                  {team.startXI?.length > 0 ? (
                    <ul>
                      {team.startXI.map(player => (
                        <li key={player.player.id}>
                          <strong># {player.player.number ? `${player.player.number}. ` : "- "}</strong>
                          {player.player.name} {player.player.pos ? ` (${player.player.pos})` : ""}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-lineup-msg">Composition non disponible</p>
                  )}

                  <h4>Remplaçants</h4>
                  {team.substitutes?.length > 0 ? (
                    <ul>
                      {team.substitutes.map(player => (
                        <li key={player.player.id}>
                          <strong># {player.player.number ? `${player.player.number}. ` : "- "}</strong>
                          {player.player.name} {player.player.pos ? ` (${player.player.pos})` : ""}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-lineup-msg">Remplaçants non disponibles</p>
                  )}
                </div>
              ))
            ) : (
              <p className="no-lineup-msg" style={{ gridColumn: "1 / -1", textAlign: "center" }}>
                Aucune composition disponible pour ce match.
              </p>
            )}
          </div>
        )}

        {activeTab === "stats" && (
          <div className="stats-tab">
            {statistics.length === 2 ? (
              [
                "Ball Possession",
                "Shots on Goal",
                "Total Shots",
                "Corner Kicks",
                "Fouls",
                "Offsides",
                "Yellow Cards",
                "Red Cards",
                "Goalkeeper Saves",
                "Passes accurate"
              ].map(statType => {
                const homeValue = getStatValue(statistics[0], statType);
                const awayValue = getStatValue(statistics[1], statType);

                const homeNum = parseInt(String(homeValue).replace("%", "")) || 0;
                const awayNum = parseInt(String(awayValue).replace("%", "")) || 0;
                const total = homeNum + awayNum || 1;

                return (
                  <div key={statType} className="stat-row">
                    <div className="stat-values">
                      <span>{homeValue}</span>
                      <span className="stat-label">{statType}</span>
                      <span>{awayValue}</span>
                    </div>
                    <div className="stat-bar-container">
                      <div className="stat-bar-home" style={{ width: `${(homeNum / total) * 100}%` }} />
                      <div className="stat-bar-away" style={{ width: `${(awayNum / total) * 100}%` }} />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="no-lineup-msg" style={{ textAlign: "center" }}>Statistiques indisponibles pour le moment.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MatchStatsModal;
